import { logEvent } from 'firebase/analytics';
import { getAnalyticsInstance } from './firebase';
import { captureException as sentryCaptureException } from './sentry';

/**
 * Environment detection utilities
 */
export const isProduction = () => {
  const hostname = window.location.hostname;
  const href = window.location.href;

  // Production: downkount.com or any Firebase production URL
  if (
    hostname === 'downkount.com' ||
    hostname === 'www.downkount.com' ||
    (href.includes('.web.app') && !href.includes('-dev.web.app'))
  ) {
    return true;
  }

  return false;
};

export const isDevelopment = () => {
  const hostname = window.location.hostname;
  const href = window.location.href;

  // Development: localhost, 127.0.0.1, or Firebase dev environment
  if (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    href.includes('downkount-dev.web.app')
  ) {
    return true;
  }

  return false;
};

export const getEnvironment = () => {
  if (isProduction()) return 'production';
  if (isDevelopment()) return 'development';
  return 'unknown';
};

/**
 * Logger class for structured logging with GA integration
 */
class Logger {
  constructor() {
    this.env = getEnvironment();
    this.isDev = isDevelopment();
    this.isProd = isProduction();
  }

  /**
   * Log info messages (only in development)
   */
  info(message, ...args) {
    if (this.isDev) {
      console.info(`[INFO] ${message}`, ...args);
    }
  }

  /**
   * Log warnings (always shown, sent to GA in production)
   */
  warn(message, ...args) {
    console.warn(`[WARN] ${message}`, ...args);

    if (this.isProd) {
      this.logToGA('warning', message, args);
    }
  }

  /**
   * Log errors (always shown, sent to GA and Sentry in production)
   */
  error(message, error, ...args) {
    console.error(`[ERROR] ${message}`, error, ...args);

    if (this.isProd) {
      // Send to Google Analytics
      this.logToGA('error', message, {
        error: error?.message || error,
        stack: error?.stack,
        ...args,
      });

      // Send to Sentry for detailed error tracking
      sentryCaptureException(error, {
        message,
        ...args,
      });
    }
  }

  /**
   * Debug logging (only in development)
   */
  debug(message, ...args) {
    if (this.isDev) {
      console.log(`[DEBUG] ${message}`, ...args);
    }
  }

  /**
   * Log to Google Analytics (production only)
   */
  logToGA(level, message, data = {}) {
    if (!this.isProd) return;

    try {
      const analytics = getAnalyticsInstance();
      if (!analytics) return;

      logEvent(analytics, 'app_error', {
        level,
        message,
        environment: this.env,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        ...data,
      });
    } catch (err) {
      // Silently fail - don't break the app if GA fails
      console.error('Failed to log to GA:', err);
    }
  }

  /**
   * Track custom events to GA (production only)
   */
  trackEvent(eventName, params = {}) {
    if (!this.isProd) {
      this.debug(`[GA Event] ${eventName}`, params);
      return;
    }

    try {
      const analytics = getAnalyticsInstance();
      if (!analytics) return;

      logEvent(analytics, eventName, {
        environment: this.env,
        timestamp: new Date().toISOString(),
        ...params,
      });
    } catch (err) {
      console.error('Failed to track event to GA:', err);
    }
  }
}

// Export singleton instance
export const logger = new Logger();

// Export convenience methods
export const logInfo = (...args) => logger.info(...args);
export const logWarn = (...args) => logger.warn(...args);
export const logError = (...args) => logger.error(...args);
export const logDebug = (...args) => logger.debug(...args);
export const trackEvent = (...args) => logger.trackEvent(...args);
